/**
 * CommunityEnablersTab — Geo Community Leaders admin UI.
 *
 * POLICY_TEAM_ROSTERS.md §4 + §5. Compact list of leaders with tray-
 * based add/edit (mirrors Full Project's RoleAssignmentModal pattern).
 *
 * List rows: small avatar + display_title + name + actions. Drag to
 * reorder. Click row → opens edit tray.
 *
 * Tray contents:
 *   - Add mode: mode tabs (Link L4E user / Add external) +
 *     ContactPicker OR ExternalMemberForm.
 *   - Edit mode: full FlipTeamMemberCard preview + editable fields
 *     (display_title, name, photo URL, contact fields, etc.).
 *
 * Save is bulk-replace via persistEnablersToApi.
 *
 * Permission badge auto-computation (POLICY §4.3) is the separate
 * chunk 2d (task #85).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  getEnablersForCommunity,
  setEnablersForCommunity,
  upsertEnabler,
  removeEnabler,
  hydrateEnablersFromApi,
  type CommunityEnabler,
} from "../../Data/CommunityConfig/communityEnablers";
import FlipTeamMemberCard from "../TeamMemberCard/FlipTeamMemberCard";
import ContactPicker from "../ContactPicker/ContactPicker";
import { Tray } from "../Tray/Tray";
import CircleCropModal from "../CircleCropModal/CircleCropModal";
import { uploadDataUrl, fileUrl } from "../../api/fileStorage";
import { mediaSrc } from "../../api/mediaUrl";
import {
  fetchCommunityMemberGrants,
  groupGrantsByUser,
  pickBadge,
  type CommunityMemberGrant,
} from "../../api/communityMemberGrants";
import type { ContactSearchResult } from "../../api/chat";
// DEV-1157 Phase 2d — Tier-1 catalog picker for slot.source_definition_guid.
// Lets the admin attach a role_definition (and its rhythm, if any) at
// slot-edit time without direct SQL.
import { fetchRoleDefinitions, type RoleDefinitionResponse } from "../../api/projectRoles";

const MAX_RAW_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
const UPLOAD_BUCKET = "community-leaders";

interface Props {
  communityGuid: string;
  /** Signal the parent modal that enabler data has been modified. */
  onDirty?: () => void;
}

/* ── GUID generator (uppercase, RFC4122 v4-style) ─────────────────── */
function generateGuid(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  }).toUpperCase();
}

/* ── Inline style tokens (CSS tokens via var()) ───────────────────── */
const styles = {
  container: {
    padding: 20,
    display: "flex",
    flexDirection: "column" as const,
    gap: 16,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: "var(--text-primary)",
  },
  subtitle: {
    fontSize: 12,
    color: "var(--text-tertiary)",
    marginBlockStart: 4,
  },
  list: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
  },
  listRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    background: "var(--bg-surface)",
    border: "1px solid var(--border-default)",
    borderRadius: 8,
    cursor: "grab",
    transition: "background 0.1s, border-color 0.1s",
  },
  listRowDragging: {
    opacity: 0.5,
  },
  listRowDragOver: {
    borderColor: "var(--accent-secondary)",
    borderStyle: "dashed" as const,
  },
  dragHandle: {
    color: "var(--text-tertiary)",
    fontSize: 14,
    cursor: "grab",
    userSelect: "none" as const,
    width: 18,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "var(--bg-elevated)",
    border: "1px solid var(--border-default)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--text-secondary)",
    fontSize: 14,
    fontWeight: 600,
    flexShrink: 0,
    objectFit: "cover" as const,
    overflow: "hidden",
  },
  rowText: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    minWidth: 0,
  },
  rowTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  rowName: {
    fontSize: 12,
    color: "var(--text-tertiary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  badge: {
    display: "inline-block",
    marginInlineStart: 8,
    padding: "1px 8px",
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text-primary)",
    background: "var(--accent-secondary)",
    borderRadius: 10,
    verticalAlign: "middle" as const,
  },
  hiddenBadge: {
    display: "inline-block",
    marginInlineStart: 8,
    padding: "1px 8px",
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text-secondary)",
    background: "var(--bg-hover)",
    border: "1px dashed var(--border-default)",
    borderRadius: 10,
    verticalAlign: "middle" as const,
  },
  rowActions: {
    display: "flex",
    gap: 6,
  },
  smallButton: {
    background: "var(--bg-hover)",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    color: "var(--text-secondary)",
    fontSize: 12,
    fontWeight: 600,
    padding: "5px 10px",
    cursor: "pointer",
  },
  dangerButton: {
    color: "#f87171",
  },
  primaryButton: {
    background: "rgba(69,187,71,0.75)",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    color: "var(--text-primary)",
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 16px",
    cursor: "pointer",
  },
  addLeaderButton: {
    background: "var(--accent-secondary)",
    border: "none",
    borderRadius: 6,
    color: "var(--text-primary)",
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 14px",
    cursor: "pointer",
  },
  emptyState: {
    padding: 24,
    textAlign: "center" as const,
    color: "var(--text-tertiary)",
    fontSize: 13,
    background: "var(--bg-surface)",
    border: "1px dashed var(--border-default)",
    borderRadius: 8,
  },
  saveBar: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    paddingBlockStart: 12,
    borderBlockStart: "1px solid var(--border-default)",
  },
  errorMsg: {
    color: "var(--color-error)",
    fontSize: 12,
  },
  trayBody: {
    padding: 20,
    display: "flex",
    flexDirection: "column" as const,
    gap: 16,
  },
  trayFooter: {
    padding: 16,
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },
  cardPreviewWrap: {
    display: "flex",
    justifyContent: "center",
    paddingBlock: 8,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text-tertiary)",
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    marginBlockEnd: 4,
  },
  inlineInput: {
    background: "var(--bg-elevated)",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    padding: "6px 10px",
    color: "var(--text-primary)",
    fontSize: 13,
    width: "100%" as const,
  },
  modeTabs: {
    display: "flex",
    gap: 8,
  },
  modeTab: {
    background: "transparent",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    color: "var(--text-secondary)",
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 14px",
    cursor: "pointer",
  },
  modeTabActive: {
    background: "var(--accent-secondary)",
    color: "var(--text-primary)",
    borderColor: "var(--accent-secondary)",
  },
} satisfies Record<string, React.CSSProperties>;

/* ── Avatar helper (small thumbnail for the list row) ─────────────── */

function smallAvatar(slot: CommunityEnabler): React.ReactNode {
  // Verified L4E members get a blue ring; external (non-L4E) members
  // and vacant slots keep the default grey border. Mirrors the larger
  // FlipTeamMemberCard variant treatment.
  const isL4e = !!slot.linked_user_guid;
  const avatarStyle: React.CSSProperties = isL4e
    ? {
        ...styles.avatar,
        border: "2px solid var(--info-text)",
        boxShadow: "0 0 0 1px rgba(56, 189, 248, 0.25)",
      }
    : styles.avatar;
  if (slot.manual_photo_url) {
    return (
      <img
        src={mediaSrc(slot.manual_photo_url) ?? undefined}
        alt=""
        style={avatarStyle}
      />
    );
  }
  // Initials fallback
  const name = slot.manual_name || slot.display_title || "?";
  const initial = name.trim().charAt(0).toUpperCase() || "?";
  return <div style={avatarStyle}>{initial}</div>;
}

/* ── Main component ───────────────────────────────────────────────── */

const CommunityEnablersTab: React.FC<Props> = ({ communityGuid, onDirty }) => {
  const [version, setVersion] = useState(0);

  // Re-render when in-memory store changes.
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    window.addEventListener("l4e_community_enablers_changed", bump);
    return () => window.removeEventListener("l4e_community_enablers_changed", bump);
  }, []);

  // Hydrate from API on first mount.
  useEffect(() => {
    void hydrateEnablersFromApi(communityGuid);
  }, [communityGuid]);

  // POLICY §4.3 — fetch active V2 community-scope grants so leader cards
  // can display a "(Admin)" / "(Owner)" badge when the linked L4E user
  // also holds platform authority. Soft-fail; badge is enrichment only.
  const [grantsByUser, setGrantsByUser] = useState<Map<string, CommunityMemberGrant[]>>(new Map());
  useEffect(() => {
    let cancelled = false;
    fetchCommunityMemberGrants(communityGuid)
      .then((rows) => {
        if (cancelled) return;
        setGrantsByUser(groupGrantsByUser(rows));
      })
      .catch((err) => {
        console.warn("[CommunityEnablersTab] member-grants fetch failed:", err);
      });
    return () => { cancelled = true; };
  }, [communityGuid]);

  const badgeFor = useCallback(
    (slot: CommunityEnabler): string | null => {
      if (!slot.linked_user_guid) return null;
      const userGrants = grantsByUser.get(String(slot.linked_user_guid).toUpperCase());
      if (!userGrants) return null;
      return pickBadge(userGrants)?.label ?? null;
    },
    [grantsByUser],
  );

  // DEV-1157 Phase 2d — fetch active role_definitions once for the picker.
  // Cached at the tab level; same data feeds every Edit tray opening.
  const [roleDefinitions, setRoleDefinitions] = useState<RoleDefinitionResponse[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchRoleDefinitions()
      .then((rows) => {
        if (cancelled) return;
        setRoleDefinitions(rows.sort((a, b) => a.label.localeCompare(b.label)));
      })
      .catch((err) => {
        console.warn("[CommunityEnablersTab] role-definitions fetch failed:", err);
      });
    return () => { cancelled = true; };
  }, []);

  const enablers = useMemo(() => {
    void version;
    return [...getEnablersForCommunity(communityGuid)].sort((a, b) => a.sort_order - b.sort_order);
  }, [communityGuid, version]);

  /* ── Mutation helpers ── */

  // Edits flow up to the parent modal via onDirty; parent's Save button
  // is the canonical commit (calls persistEnablersToApi as part of its
  // flow). No local Save/Discard buttons — that would duplicate the
  // parent and confuse users.
  const markDirty = useCallback(() => {
    onDirty?.();
  }, [onDirty]);

  const handleFieldChange = useCallback(
    (slot: CommunityEnabler, patch: Partial<CommunityEnabler>) => {
      upsertEnabler(communityGuid, {
        ...slot,
        ...patch,
        updated_at: new Date().toISOString(),
      });
      markDirty();
    },
    [communityGuid, markDirty],
  );

  const handleRemove = useCallback(
    (slot: CommunityEnabler) => {
      const label = slot.manual_name || slot.display_title || "this leader";
      if (!window.confirm(`Remove ${label}?`)) return;
      removeEnabler(communityGuid, slot.role_slot_guid);
      markDirty();
    },
    [communityGuid, markDirty],
  );

  /* ── Drag-to-reorder (HTML5 pattern matching NewsCurateModal) ── */

  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
    dragIndexRef.current = index;
    setDraggingIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      const srcIndex = dragIndexRef.current;
      setDragOverIndex(null);
      setDraggingIndex(null);
      dragIndexRef.current = null;
      if (srcIndex === null || srcIndex === dropIndex) return;

      const next = [...enablers];
      const [moved] = next.splice(srcIndex, 1);
      next.splice(dropIndex, 0, moved);
      const restamped = next.map((e, i) => ({ ...e, sort_order: i + 1 }));
      setEnablersForCommunity(communityGuid, restamped);
      markDirty();
    },
    [enablers, communityGuid, markDirty],
  );

  const handleDragEnd = useCallback(() => {
    setDragOverIndex(null);
    setDraggingIndex(null);
    dragIndexRef.current = null;
  }, []);

  /* ── Avatar upload state (CircleCropModal driven) ── */

  // When the admin picks a file, we capture it as a pending crop and a
  // callback that knows what to do with the resulting URL (set on form
  // for Add mode, set on slot for Edit mode). The CircleCropModal then
  // floats over the tray at the parent level.
  const [pendingCrop, setPendingCrop] = useState<
    | null
    | {
        src: string;
        w: number;
        h: number;
        uploadKey: string;
        onPersistUrl: (url: string) => void;
      }
  >(null);

  const handleAvatarFile = useCallback(
    (file: File, uploadKey: string, onPersistUrl: (url: string) => void) => {
      if (file.size > MAX_RAW_FILE_BYTES) {
        alert(`Image too large (max ${MAX_RAW_FILE_BYTES / 1024 / 1024}MB).`);
        return;
      }
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        setPendingCrop({
          src: url,
          w: img.naturalWidth,
          h: img.naturalHeight,
          uploadKey,
          onPersistUrl,
        });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        alert("Failed to load image.");
      };
      img.src = url;
    },
    [],
  );

  const handleAvatarCropConfirm = useCallback(
    async (dataUrl: string) => {
      if (!pendingCrop) return;
      const { uploadKey, onPersistUrl } = pendingCrop;
      // Free the object URL from the original file load.
      URL.revokeObjectURL(pendingCrop.src);
      setPendingCrop(null);

      const ts = Date.now();
      try {
        const result = await uploadDataUrl(dataUrl, UPLOAD_BUCKET, uploadKey, `avatar-${ts}.png`);
        const storedUrl = `${fileUrl(result.key)}?v=${ts}`;
        onPersistUrl(storedUrl);
      } catch (err) {
        console.warn("[CommunityEnablersTab] Avatar upload failed; falling back to data URL.", err);
        // Fallback: store the data URL inline so the admin still sees their crop.
        // Persistence via persistEnablersToApi will round-trip it (large but works).
        onPersistUrl(dataUrl);
      }
    },
    [pendingCrop],
  );

  const handleAvatarCropCancel = useCallback(() => {
    if (pendingCrop) URL.revokeObjectURL(pendingCrop.src);
    setPendingCrop(null);
  }, [pendingCrop]);

  /* ── Tray state ── */

  /** Tray mode:
   *   - null: closed
   *   - 'add': adding a new leader
   *   - { editingGuid }: editing the leader with that role_slot_guid
   */
  const [trayMode, setTrayMode] = useState<null | "add" | { editingGuid: string }>(null);

  const trayOpen = trayMode !== null;
  const editingSlot = useMemo(() => {
    if (trayMode && typeof trayMode === "object" && trayMode.editingGuid) {
      return enablers.find((e) => e.role_slot_guid === trayMode.editingGuid) || null;
    }
    return null;
  }, [trayMode, enablers]);

  const closeTray = useCallback(() => setTrayMode(null), []);

  /* ── Add member callbacks ── */

  const linkedGuids = useMemo(
    () => enablers.map((e) => e.linked_user_guid || "").filter(Boolean) as string[],
    [enablers],
  );

  const maxSort = useMemo(
    () => enablers.reduce((acc, e) => Math.max(acc, e.sort_order), 0),
    [enablers],
  );

  const handleAddL4E = useCallback(
    (contact: ContactSearchResult) => {
      const newEnabler: CommunityEnabler = {
        role_slot_guid: generateGuid(),
        community_entity_guid: communityGuid.toUpperCase(),
        display_title: "Team Member",
        sort_order: maxSort + 1,
        linked_user_guid: contact.userGuid,
        manual_name: contact.displayName || contact.screenName || undefined,
        manual_photo_url: contact.profileImageUrl || undefined,
        manual_email: contact.email || undefined,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      upsertEnabler(communityGuid, newEnabler);
      markDirty();
      // Switch tray to edit mode on the newly added leader so admin can
      // set display_title before save (default "Team Member" is rarely right).
      setTrayMode({ editingGuid: newEnabler.role_slot_guid });
    },
    [communityGuid, maxSort, markDirty],
  );

  const handleAddExternal = useCallback(
    (form: ExternalForm) => {
      if (!form.name.trim()) return;
      const newEnabler: CommunityEnabler = {
        role_slot_guid: generateGuid(),
        community_entity_guid: communityGuid.toUpperCase(),
        display_title: form.title.trim() || "Team Member",
        sort_order: maxSort + 1,
        linked_user_guid: null,
        manual_name: form.name.trim(),
        manual_title: form.title.trim() || undefined,
        manual_photo_url: form.photoUrl.trim() || undefined,
        manual_email: form.email.trim() || undefined,
        manual_phone: form.phone.trim() || undefined,
        external_profile_url: form.profileUrl.trim() || undefined,
        notes: form.notes.trim() || undefined,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      upsertEnabler(communityGuid, newEnabler);
      markDirty();
      closeTray();
    },
    [communityGuid, maxSort, markDirty, closeTray],
  );

  /* ── Render ── */

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Leaders</div>
          <div style={styles.subtitle}>
            Public-facing roster of this community's leaders. Drag to reorder.
            Permissions are managed separately on the Audience &amp; Permissions tab.
          </div>
        </div>
        <button
          type="button"
          style={styles.addLeaderButton}
          onClick={() => setTrayMode("add")}
        >
          + Add leader
        </button>
      </div>

      {enablers.length === 0 ? (
        <div style={styles.emptyState}>
          No leaders yet. Click <em>Add leader</em> to add the first one.
        </div>
      ) : (
        <div style={styles.list}>
          {enablers.map((slot, idx) => (
            <LeaderListRow
              key={slot.role_slot_guid}
              slot={slot}
              index={idx}
              badge={badgeFor(slot)}
              dragging={draggingIndex === idx}
              dragOver={dragOverIndex === idx && draggingIndex !== idx}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              onEdit={() => setTrayMode({ editingGuid: slot.role_slot_guid })}
              onRemove={() => handleRemove(slot)}
            />
          ))}
        </div>
      )}

      {/* ── Tray ── */}
      <Tray
        open={trayOpen}
        onClose={closeTray}
        title={
          trayMode === "add"
            ? "Add leader"
            : editingSlot
              ? `Edit ${editingSlot.manual_name || editingSlot.display_title || "leader"}`
              : "Edit leader"
        }
      >
        {trayMode === "add" && (
          <AddLeaderTrayBody
            onAddL4E={handleAddL4E}
            onAddExternal={handleAddExternal}
            onCancel={closeTray}
            excludeGuids={linkedGuids}
            onAvatarFile={handleAvatarFile}
          />
        )}
        {editingSlot && (
          <EditLeaderTrayBody
            slot={editingSlot}
            badge={badgeFor(editingSlot)}
            roleDefinitions={roleDefinitions}
            onFieldChange={(patch) => handleFieldChange(editingSlot, patch)}
            onRemove={() => {
              handleRemove(editingSlot);
              closeTray();
            }}
            onClose={closeTray}
            onAvatarFile={handleAvatarFile}
          />
        )}
      </Tray>

      {/* ── Avatar crop modal — portal-mounted at document.body so its
          z-index isn't capped by the parent settings modal's stacking
          context. Without the portal the modal renders behind the Tray
          and clicks on its Save button bubble through to the leader
          list underneath. Wrapper also stops mousedown/click bubbling
          as belt-and-suspenders. ── */}
      {pendingCrop &&
        createPortal(
          <div
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <CircleCropModal
              imageSrc={pendingCrop.src}
              naturalW={pendingCrop.w}
              naturalH={pendingCrop.h}
              onConfirm={handleAvatarCropConfirm}
              onCancel={handleAvatarCropCancel}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CommunityEnablersTab;

/* ──────────────────────────────────────────────────────────────────
 *  Sub-components
 * ────────────────────────────────────────────────────────────────── */

interface LeaderListRowProps {
  slot: CommunityEnabler;
  index: number;
  badge: string | null;
  dragging: boolean;
  dragOver: boolean;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onEdit: () => void;
  onRemove: () => void;
}

function LeaderListRow({
  slot,
  index,
  badge,
  dragging,
  dragOver,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onEdit,
  onRemove,
}: LeaderListRowProps) {
  const rowStyle: React.CSSProperties = {
    ...styles.listRow,
    ...(dragging ? styles.listRowDragging : {}),
    ...(dragOver ? styles.listRowDragOver : {}),
  };
  const titleLabel = (slot.display_title || "").trim() || "Team Member";
  const nameLabel = slot.manual_name || "(unnamed)";

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={(e) => onDrop(e, index)}
      onDragEnd={onDragEnd}
      style={rowStyle}
      onClick={onEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
    >
      <span style={styles.dragHandle}>⋮⋮</span>
      {smallAvatar(slot)}
      <div style={styles.rowText}>
        <div style={styles.rowTitle}>
          {titleLabel}
          {badge && <span style={styles.badge}>{badge}</span>}
          {slot.is_hidden && <span style={styles.hiddenBadge}>Hidden</span>}
        </div>
        <div style={styles.rowName}>{nameLabel}</div>
      </div>
      <div style={styles.rowActions} onClick={(e) => e.stopPropagation()}>
        <button type="button" style={styles.smallButton} onClick={onEdit}>
          Edit
        </button>
        <button
          type="button"
          style={{ ...styles.smallButton, ...styles.dangerButton }}
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

/* ── Tray body: Add leader ────────────────────────────────────────── */

interface AddLeaderTrayBodyProps {
  onAddL4E: (contact: ContactSearchResult) => void;
  onAddExternal: (form: ExternalForm) => void;
  onCancel: () => void;
  excludeGuids: string[];
  onAvatarFile: (file: File, uploadKey: string, onPersistUrl: (url: string) => void) => void;
}

function AddLeaderTrayBody({
  onAddL4E,
  onAddExternal,
  onCancel,
  excludeGuids,
  onAvatarFile,
}: AddLeaderTrayBodyProps) {
  const [mode, setMode] = useState<"l4e" | "external">("l4e");

  return (
    <>
      <Tray.Body>
        <div style={styles.trayBody}>
          <div style={styles.modeTabs}>
            <button
              type="button"
              style={mode === "l4e" ? { ...styles.modeTab, ...styles.modeTabActive } : styles.modeTab}
              onClick={() => setMode("l4e")}
            >
              Link an L4E user
            </button>
            <button
              type="button"
              style={mode === "external" ? { ...styles.modeTab, ...styles.modeTabActive } : styles.modeTab}
              onClick={() => setMode("external")}
            >
              Add external member
            </button>
          </div>
          {mode === "l4e" ? (
            <ContactPicker
              onSelect={onAddL4E}
              onCancel={onCancel}
              excludeGuids={excludeGuids}
              placeholder="Search L4E contacts by name…"
            />
          ) : (
            <ExternalMemberForm
              onSubmit={onAddExternal}
              onCancel={onCancel}
              onAvatarFile={onAvatarFile}
            />
          )}
        </div>
      </Tray.Body>
    </>
  );
}

/* ── Tray body: Edit leader ───────────────────────────────────────── */

interface EditLeaderTrayBodyProps {
  slot: CommunityEnabler;
  badge: string | null;
  /** DEV-1157 Phase 2d — Tier-1 catalog for the source-definition picker. */
  roleDefinitions: RoleDefinitionResponse[];
  onFieldChange: (patch: Partial<CommunityEnabler>) => void;
  onRemove: () => void;
  onClose: () => void;
  onAvatarFile: (file: File, uploadKey: string, onPersistUrl: (url: string) => void) => void;
}

function EditLeaderTrayBody({ slot, badge, roleDefinitions, onFieldChange, onRemove, onClose, onAvatarFile }: EditLeaderTrayBodyProps) {
  // DEV-1157 Phase 2d — currently-selected definition and rhythm-attached flag.
  const selectedDef = slot.source_definition_guid
    ? roleDefinitions.find((d) => d.definitionGuid === slot.source_definition_guid)
    : undefined;
  const hasRhythm = !!selectedDef?.roleRhythmGuid;

  const isLinked = !!slot.linked_user_guid;
  const isVacant = !slot.linked_user_guid && !slot.manual_name;

  return (
    <>
      <Tray.Body>
        <div style={styles.trayBody}>
          {/* Live preview — same FlipTeamMemberCard the public sees.
              Badge surfaces on the back face only, not appended to the
              front-face title, so the admin sees exactly what the
              public sees. */}
          <div style={styles.cardPreviewWrap}>
            <FlipTeamMemberCard
              displayName={slot.manual_name || ""}
              photoUrl={slot.manual_photo_url}
              roleTitle={(slot.display_title || "").trim() || "Team Member"}
              linkedUserGuid={slot.linked_user_guid}
              userGuid={slot.linked_user_guid}
              contactEmail={slot.manual_email}
              contactProfileUrl={slot.external_profile_url}
              backFaceBadge={badge}
              isVacant={isVacant}
              avatarVariant={
                isVacant ? "default"
                : slot.linked_user_guid ? "l4e"
                : "expanded"
              }
              splitName
              size="lg"
            />
          </div>

          <div style={styles.fieldGroup}>
            <div>
              <div style={styles.fieldLabel}>Display title</div>
              <input
                type="text"
                value={slot.display_title || ""}
                onChange={(e) => onFieldChange({ display_title: e.target.value })}
                placeholder="e.g. Mayor, Town Manager, Council Member"
                style={styles.inlineInput}
              />
            </div>

            {/* DEV-1157 Phase 2d — Tier-1 catalog picker. Optional.
                When set + the definition has a role_rhythm_guid, Phase 1c's
                hook will materialize rhythm instances for whoever fills the
                slot. */}
            <div>
              <div style={styles.fieldLabel}>Role definition (Tier-1 catalog)</div>
              <select
                value={slot.source_definition_guid || ""}
                onChange={(e) => onFieldChange({
                  source_definition_guid: e.target.value || null,
                })}
                style={styles.inlineInput}
              >
                <option value="">(none — descriptive slot only)</option>
                {roleDefinitions.map((d) => (
                  <option key={d.definitionGuid} value={d.definitionGuid}>
                    {d.label}
                  </option>
                ))}
              </select>
              {selectedDef && (
                <div style={{
                  fontSize: 12,
                  color: hasRhythm ? "var(--color-success)" : "var(--text-tertiary)",
                  marginBlockStart: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}>
                  {hasRhythm
                    ? <>✓ Rhythm attached — recurring tasks will materialize on user link.</>
                    : <>No rhythm attached — slot is descriptive only.</>}
                </div>
              )}
            </div>

            {/* Hidden-from-public toggle. Applies to all leaders. Use cases:
                anonymity request, or transition periods (someone stepping
                down before a replacement is appointed). Admin Settings tab
                still shows the row with a "Hidden" indicator. */}
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "10px 12px",
                background: "var(--bg-elevated)",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={!!slot.is_hidden}
                onChange={(e) => onFieldChange({ is_hidden: e.target.checked })}
                style={{ marginBlockStart: 2 }}
              />
              <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                  Hide from public Key Community Leaders
                </span>
                <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
                  Use for anonymity requests or transition periods. Admins still
                  see this leader in Settings.
                </span>
              </span>
            </label>

            {!isLinked && (
              <>
                {/* Avatar editor — mirrors the L4E user profile layout */}
                <AvatarUploader
                  name={slot.manual_name}
                  currentUrl={slot.manual_photo_url}
                  onPickFile={(file) =>
                    onAvatarFile(file, slot.role_slot_guid, (url) =>
                      onFieldChange({ manual_photo_url: url }),
                    )
                  }
                  onPasteUrl={(url) => onFieldChange({ manual_photo_url: url })}
                  onClear={() => onFieldChange({ manual_photo_url: undefined })}
                />
                <div>
                  <div style={styles.fieldLabel}>Display name</div>
                  <input
                    type="text"
                    value={slot.manual_name || ""}
                    onChange={(e) => onFieldChange({ manual_name: e.target.value })}
                    placeholder="Full name"
                    style={styles.inlineInput}
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <div style={styles.fieldLabel}>Email</div>
                    <input
                      type="email"
                      value={slot.manual_email || ""}
                      onChange={(e) => onFieldChange({ manual_email: e.target.value })}
                      placeholder="email@example.com"
                      style={styles.inlineInput}
                    />
                  </div>
                  <div>
                    <div style={styles.fieldLabel}>Phone</div>
                    <input
                      type="text"
                      value={slot.manual_phone || ""}
                      onChange={(e) => onFieldChange({ manual_phone: e.target.value })}
                      placeholder="+1 555 555 5555"
                      style={styles.inlineInput}
                    />
                  </div>
                </div>
                <div>
                  <div style={styles.fieldLabel}>External profile URL</div>
                  <input
                    type="url"
                    value={slot.external_profile_url || ""}
                    onChange={(e) => onFieldChange({ external_profile_url: e.target.value })}
                    placeholder="https://example.com/profile"
                    style={styles.inlineInput}
                  />
                </div>
                <div>
                  <div style={styles.fieldLabel}>Notes (admin-internal)</div>
                  <input
                    type="text"
                    value={slot.notes || ""}
                    onChange={(e) => onFieldChange({ notes: e.target.value })}
                    placeholder="Optional"
                    style={styles.inlineInput}
                  />
                </div>
              </>
            )}

            {isLinked && (
              <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
                Linked to an L4E user — name, photo, and contact info come from their profile.
                Display title is the only field this admin can override.
              </div>
            )}
          </div>
        </div>
      </Tray.Body>
      <Tray.Footer>
        <div style={styles.trayFooter}>
          <button
            type="button"
            style={{ ...styles.smallButton, ...styles.dangerButton }}
            onClick={onRemove}
          >
            Remove leader
          </button>
          <button type="button" style={styles.primaryButton} onClick={onClose}>
            Done
          </button>
        </div>
      </Tray.Footer>
    </>
  );
}

/* ── External member form (no-L4E entry path) ─────────────────────── */

interface ExternalForm {
  name: string;
  title: string;
  email: string;
  phone: string;
  photoUrl: string;
  profileUrl: string;
  notes: string;
}

interface ExternalMemberFormProps {
  onSubmit: (form: ExternalForm) => void;
  onCancel: () => void;
  onAvatarFile: (file: File, uploadKey: string, onPersistUrl: (url: string) => void) => void;
}

function ExternalMemberForm({ onSubmit, onCancel, onAvatarFile }: ExternalMemberFormProps) {
  // Pre-generate the role_slot_guid so we have a stable upload key for
  // any avatar the admin picks BEFORE the leader is actually upserted.
  const pendingGuidRef = useRef<string>(generateGuid());

  const [form, setForm] = useState<ExternalForm>({
    name: "",
    title: "",
    email: "",
    phone: "",
    photoUrl: "",
    profileUrl: "",
    notes: "",
  });
  const [error, setError] = useState<string | null>(null);

  const update = (patch: Partial<ExternalForm>) => setForm((f) => ({ ...f, ...patch }));

  const submit = () => {
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    setError(null);
    onSubmit(form);
  };

  return (
    <div style={styles.fieldGroup}>
      {/* Avatar editor at the top — mirrors the L4E user profile layout */}
      <AvatarUploader
        name={form.name}
        currentUrl={form.photoUrl}
        onPickFile={(file) =>
          onAvatarFile(file, pendingGuidRef.current, (url) => update({ photoUrl: url }))
        }
        onPasteUrl={(url) => update({ photoUrl: url })}
        onClear={() => update({ photoUrl: "" })}
      />
      <div>
        <div style={styles.fieldLabel}>Name *</div>
        <input
          type="text"
          value={form.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="Full name"
          style={styles.inlineInput}
          autoFocus
        />
      </div>
      <div>
        <div style={styles.fieldLabel}>Display title</div>
        <input
          type="text"
          value={form.title}
          onChange={(e) => update({ title: e.target.value })}
          placeholder="e.g. Mayor, Town Manager"
          style={styles.inlineInput}
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <div style={styles.fieldLabel}>Email</div>
          <input
            type="email"
            value={form.email}
            onChange={(e) => update({ email: e.target.value })}
            placeholder="email@example.com"
            style={styles.inlineInput}
          />
        </div>
        <div>
          <div style={styles.fieldLabel}>Phone</div>
          <input
            type="text"
            value={form.phone}
            onChange={(e) => update({ phone: e.target.value })}
            placeholder="+1 555 555 5555"
            style={styles.inlineInput}
          />
        </div>
      </div>
      <div>
        <div style={styles.fieldLabel}>External profile URL (website / LinkedIn / etc.)</div>
        <input
          type="url"
          value={form.profileUrl}
          onChange={(e) => update({ profileUrl: e.target.value })}
          placeholder="https://example.com/profile"
          style={styles.inlineInput}
        />
      </div>
      <div>
        <div style={styles.fieldLabel}>Notes (admin-internal)</div>
        <input
          type="text"
          value={form.notes}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder="Optional"
          style={styles.inlineInput}
        />
      </div>
      {error && <div style={styles.errorMsg}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" style={styles.smallButton} onClick={onCancel}>Cancel</button>
        <button type="button" style={styles.primaryButton} onClick={submit}>Add</button>
      </div>
    </div>
  );
}

/* ── AvatarUploader sub-component ─────────────────────────────────── */
/* Mirrors the L4E user profile avatar editing UX: circle preview +    */
/* Upload / Paste URL / Remove controls.                                */

interface AvatarUploaderProps {
  name?: string;
  currentUrl?: string;
  onPickFile: (file: File) => void;
  onPasteUrl: (url: string) => void;
  onClear: () => void;
}

function AvatarUploader({ name, currentUrl, onPickFile, onPasteUrl, onClear }: AvatarUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");

  const initial = ((name || "").trim().charAt(0) || "?").toUpperCase();

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <div
        style={{
          width: 96,
          height: 96,
          borderRadius: "50%",
          background: "var(--bg-elevated)",
          border: "1px solid var(--border-default)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-secondary)",
          fontSize: 32,
          fontWeight: 600,
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {currentUrl ? (
          <img
            src={currentUrl}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          initial
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
        <div style={styles.fieldLabel}>Photo</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button
            type="button"
            style={styles.smallButton}
            onClick={() => fileInputRef.current?.click()}
          >
            {currentUrl ? "Change photo" : "Upload photo"}
          </button>
          <button
            type="button"
            style={styles.smallButton}
            onClick={() => {
              setShowUrlInput((v) => !v);
              setUrlDraft(currentUrl || "");
            }}
          >
            Paste URL
          </button>
          {currentUrl && (
            <button
              type="button"
              style={{ ...styles.smallButton, ...styles.dangerButton }}
              onClick={onClear}
            >
              Remove
            </button>
          )}
        </div>
        {showUrlInput && (
          <div style={{ display: "flex", gap: 6 }}>
            <input
              type="url"
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              placeholder="https://example.com/photo.jpg"
              style={styles.inlineInput}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onPasteUrl(urlDraft.trim());
                  setShowUrlInput(false);
                } else if (e.key === "Escape") {
                  setShowUrlInput(false);
                }
              }}
            />
            <button
              type="button"
              style={styles.smallButton}
              onClick={() => {
                onPasteUrl(urlDraft.trim());
                setShowUrlInput(false);
              }}
            >
              Save
            </button>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPickFile(f);
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}
